import { useMemo, useRef } from 'react';
import {
  DEFAULT_TIMELINE,
  REPORT_PERIODS,
  TIMELINE_DIMENSIONS_FOR,
  TIMELINE_EXTRA_DIMENSIONS_FOR,
  TIMELINE_EXTRA_KINDS,
  TIMELINE_GRANULARITIES,
  WORKDAY_METRICS,
  ALLOCATION_METRICS,
  periodOf,
  periodRange,
  type Metafield,
  type Person,
  type Project,
  type ReportDefinition,
  type ReportDimension,
  type ReportMetric,
  type ReportPeriod,
  type ReportSnapshot,
  type TimelineDimension,
  type TimelineGranularity,
  type TimelineSpec,
} from '@forecastify/shared';
import {
  Button,
  DropdownMenu,
  Icon,
  MultiSelect,
  Segmented,
  Select,
  Switch,
  TextInput,
  Tooltip,
  type MenuItem,
} from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { usePageHeader } from '../page-header';
import { useSidebarContext } from '../sidebar/use-sidebar';
import { ReportResult, dimensionLabel } from './report-result';
import { metricLabel, sourceLabel } from './reports-screen';
import { DateField } from '../date-field';
import {
  CHART_GROUP_OF,
  DIMENSIONS_FOR,
  MONEY_SOURCES,
  REPORT_CHARTS,
  REPORT_SOURCES,
  chartRefusal,
  isMoneyMetric,
  type ReportChart,
  type ReportSource,
} from '@forecastify/shared';
import { chartLabel, refusalNote } from './report-charts';
import { ReportListView, filterLabel, filterOptions, type ReportListOptions } from './report-list';
import { ReportTimelineView, extraHeading, timelineDimensionLabel } from './report-timeline';
import {
  DEFAULT_REPORT_COLUMNS,
  REPORT_FILTERS_FOR,
  listSpecOf,
  type ReportFilterKey,
  type ReportFilters,
  type ReportLayout,
} from '@forecastify/shared';
import { recommendCharts, type RecommendationReason } from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Riportépítő — `markup.html` 1282–1497, `main.jsx` 3580–3654.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A filter bar, a result pane and a 300px control rail with four sections: Mértékek, Dimenziók,
 * Megjelenítés, Szűrők.
 *
 * ⚠️⚠️ **"NO METRIC YET" IS A BUILDER STATE, NOT A DEFINITION STATE.** The mockup's `rep.agg` starts
 * as `''` and `repRailDim` greys out every other section until one is chosen — a deliberate
 * onboarding order (metric, then dimension, then filters). Our `metric` is a required enum, so the
 * draft carries `metric: null` and only becomes a runnable definition once it is set. Making the
 * wire type nullable to match a wizard step would have put a builder's half-finished state into the
 * database.
 *
 * ⚠️ **The period label is DERIVED from the two dates, never stored.** The mockup keeps `repPeriod`
 * in component state, so reopening a saved report whose range is exactly this month showed
 * *"Egyéni"*. `periodOf` recomputes it, so the label cannot disagree with the dates.
 */

/** The draft the builder edits — a definition whose metric may not be chosen yet. */
export interface BuilderDraft extends Omit<ReportDefinition, 'metric'> {
  metric: ReportMetric | null;
}

/**
 * Which filter rows the rail is showing — the mockup's `repFilters`.
 *
 * ⚠️ 16 Oct 2026 · the mockup's three (project, person, metafield) became every filter the source
 * honours, several values each, for both layouts. A report saved with the old single project or
 * person reopens with it as one value of the new row (`reports-route.tsx`).
 */
export type FilterKey = ReportFilterKey | 'metafield';

/**
 * The draft as a runnable definition, or null while it is not one.
 *
 * ⚠️⚠️ **A HALF-SET METAFIELD FILTER IS NORMALISED TO NO FILTER, NOT SENT.** The contract's
 * refinement pairs `filterMetafieldId` and `filterMetafieldValue` — either both or neither — and the
 * rail necessarily passes through "field chosen, value not yet" on the way. Sending that would make
 * every keystroke of the picker a `validation_failed`; dropping it means the report simply is not
 * filtered until the pair is complete, which is what the screen already looks like.
 */
export function asDefinition(draft: BuilderDraft): ReportDefinition | null {
  /* ⚠️ 16 Oct 2026 · a LIST needs no measure to run — its columns are what it shows. The schema
     still wants one (it is what the summary reopens on), so an unchosen one is `hours`. */
  if (draft.metric === null && draft.layout !== 'list') return null;
  const half = draft.filterMetafieldValue === null;
  return {
    ...draft,
    metric: draft.metric ?? 'hours',
    filterMetafieldId: half ? null : draft.filterMetafieldId,
    filterMetafieldValue: half ? null : draft.filterMetafieldValue,
  };
}

function periodLabelOf(t: Translate, period: ReportPeriod): string {
  /* One `t()` per branch with a literal key. */
  if (period === 'week') return t('report.period.week');
  if (period === 'month') return t('report.period.month');
  if (period === 'quarter') return t('report.period.quarter');
  if (period === 'year') return t('report.period.year');
  /* 17 Oct 2026 · the rolling ones. */
  if (period === 'last3Months') return t('report.period.last3Months');
  if (period === 'last6Months') return t('report.period.last6Months');
  if (period === 'last12Months') return t('report.period.last12Months');
  if (period === 'all') return t('report.period.all');
  return t('report.period.custom');
}

export interface BuilderScreenProps {
  readonly name: string;
  readonly draft: BuilderDraft;
  readonly filters: readonly FilterKey[];
  readonly snapshot: ReportSnapshot | null;
  readonly ranAt: string | null;
  /** Null for an unsaved report — the header shows Save rather than the kebab. */
  readonly reportId: string | null;
  readonly today: string;

  readonly projects: readonly Project[];
  readonly people: readonly Person[];
  /** The `select` metafields on tasks — the only kind that can group or filter (see the service). */
  readonly metafields: readonly Metafield[];

  /**
   * C60 · ⚠️ **Whether the caller holds `report.money`.** The two money measures stay LISTED when
   * they do not — visible and disabled, with the reason — because somebody who cannot find the
   * option at all concludes the product cannot do it and asks nobody.
   */
  readonly canMoney: boolean;
  /** 16 Oct 2026 · the list layout's filter and column choices. */
  readonly listOptions: ReportListOptions;
  /** Bumped by Refresh, so a saved LIST re-reads (it stores nothing to replace). */
  readonly listReloadKey: number;
  /** Null when the caller may not export, or the report is not saved yet. */
  readonly onExport: (() => void) | null;
  /**
   * C60 · ⚠️ **The run was REFUSED for the money permission**, which is a different state from "no
   * measure yet" and from "it failed". A saved report is the only way to reach it.
   */
  readonly moneyLocked: boolean;
  readonly showControls: boolean;
  readonly showTable: boolean;
  readonly canManage: boolean;
  readonly serverError: string | null;
  readonly isRunning: boolean;

  readonly onName: (value: string) => void;
  /** 3 Nov 2026 · the name field lost focus or took Enter — a saved report renames there. */
  readonly onNameCommit: () => void;
  /** Escape in the name field — the stored name comes back. */
  readonly onNameCancel: () => void;
  readonly onDraft: (patch: Partial<BuilderDraft>) => void;
  readonly onFilters: (keys: readonly FilterKey[]) => void;
  readonly onShowControls: (value: boolean) => void;
  readonly onShowTable: (value: boolean) => void;
  readonly onBack: () => void;
  readonly onSave: () => void;
  readonly onRefresh: () => void;
  readonly onDuplicate: () => void;
  readonly onDelete: () => void;
}

export function BuilderScreen(props: BuilderScreenProps) {
  const t = useT();
  const { draft, filters, snapshot } = props;
  /* ⚠️ The header's handlers read the CURRENT props — see the name field. Assigned during render
     for the reason `usePageHeader` gives for its own ref. */
  const latest = useRef(props);
  latest.current = props;
  /* Escape blurs the name field too, and that blur must not save what Escape just discarded. */
  const cancelling = useRef(false);
  const sidebar = useSidebarContext();
  const pinned = props.reportId !== null && sidebar?.isReportPinned(props.reportId) === true;

  /* ⚠️ 17 Oct 2026 · a kept preset names itself; otherwise the label is derived from the dates. */
  const period =
    draft.period !== undefined && draft.period !== 'custom'
      ? draft.period
      : periodOf({ from: draft.periodFrom, to: draft.periodTo }, props.today);

  /* ── the header's kebab ────────────────────────────────────────────────────────────────── */

  const layout = listSpecOf({ ...draft, metric: draft.metric ?? 'hours' }).layout;
  const isList = layout === 'list';
  /* 17 Oct 2026 · *Időbeli* — rows by periods. */
  const isTimeline = layout === 'timeline';
  const spec: TimelineSpec = draft.timeline ?? DEFAULT_TIMELINE;

  const kebab: MenuItem[] = [
    {
      label: t('report.action.duplicate'),
      icon: 'copy',
      onSelect: props.onDuplicate,
    },
    /* 21 Oct 2026 · export is the header's own button now, beside Mentés. */
    { type: 'separator' },
    {
      label: t('report.action.delete'),
      icon: 'trash',
      destructive: true,
      onSelect: props.onDelete,
    },
  ];

  /* ── the rail's menus ──────────────────────────────────────────────────────────────────── */

  const metricMenu: MenuItem[] = useMemo(() => {
    const items: {
      value: ReportMetric;
      icon: 'clock' | 'list-checks' | 'chart-column' | 'timer' | 'credit-card';
    }[] = [
      { value: 'hours', icon: 'clock' },
      /* ⚠️ 17 Oct 2026 · a working day is not counted — it is expected, logged, missing or leave. */
      ...(draft.source === 'workday'
        ? []
        : [{ value: 'count' as const, icon: 'list-checks' as const }]),
    ];
    if (draft.source === 'workday') {
      items.push(
        { value: 'expected', icon: 'clock' },
        { value: 'missing', icon: 'timer' },
        { value: 'leave', icon: 'timer' },
        { value: 'missingCost', icon: 'credit-card' },
      );
    }
    /**
     * ⚠️ **`estimate` only for the task source, and the mockup agrees** — its `repAggOptions` appends
     * it only then, and `setRepSrc` demotes it to `hours` on a switch away. A worklog has no
     * estimate, and the server refuses the combination anyway.
     */
    if (draft.source === 'task') items.push({ value: 'estimate', icon: 'chart-column' });

    /**
     * ⚠⚠ **AND THE MIRROR IMAGE: THE TWO OVERTIME MEASURES ARE WORKLOG-ONLY.** A claim hangs off
     * an hour (`0111`), so a task report has nothing to sum — the mockup's own menu disables them
     * for every other source, and the schema refuses the combination at the boundary.
     *
     * ⚠️ **They are two entries, not one, and the difference is the multiplier.** Four hours at
     * 1.5× is 4 and 6: *"how much did people work beyond their schedule"* and *"what do we owe for
     * it"* are different questions.
     */
    if (draft.source === 'worklog') {
      items.push({ value: 'overtime', icon: 'timer' }, { value: 'overtimeCost', icon: 'timer' });
    }

    /**
     * C60 · ⚠⚠ **THE MONEY MEASURES ARE LISTED EVEN WHEN LOCKED, AND SAY SO.** The mockup labels
     * them *"Pénzes mértékek — jogosultság kell"* and leaves them visible: somebody who cannot find
     * the option at all concludes the product cannot do it, and asks nobody. A disabled row with a
     * reason is how they learn to ask their admin.
     */
    if (MONEY_SOURCES.includes(draft.source)) {
      items.push(
        { value: 'revenue', icon: 'credit-card' },
        { value: 'budget', icon: 'credit-card' },
      );
    }

    return items.map((item) => ({
      label: isMoneyMetric(item.value)
        ? `${metricLabel(t, item.value)}${props.canMoney ? '' : ` · ${t('report.money.locked')}`}`
        : metricLabel(t, item.value),
      icon: item.icon,
      tourId: `metric-${item.value}`,
      disabled: isMoneyMetric(item.value) && !props.canMoney,
      checked: draft.metric === item.value,
      onSelect: () => {
        props.onDraft({ metric: item.value });
      },
    }));
  }, [draft.source, draft.metric, props, t]);

  /**
   * ⚠⚠ **FIVE SOURCES AS OF C60, AND SWITCHING ONE CAN INVALIDATE THE REST OF THE DEFINITION.**
   * A dimension the new source cannot reach would group everything into `'none'`; a money measure
   * over a worklog has no revenue to sum. Both are refused by the schema, so the picker repairs the
   * draft as it switches rather than letting somebody build a body the server will reject.
   */
  const sourceMenu: MenuItem[] = REPORT_SOURCES.map((value) => ({
    label: sourceLabel(t, value),
    icon: SOURCE_ICON[value],
    checked: draft.source === value,
    onSelect: () => {
      /* ⚠️ The mockup's `setRepSrc` demotes in the same spirit — it just had fewer ways to be
         wrong. */
      const dimensions = DIMENSIONS_FOR[value];
      props.onFilters(
        filters.filter(
          (key) =>
            key === 'metafield' ||
            compatibleFilters({ [key]: ['x'] }, draft.source, value)[key] !== undefined,
        ),
      );
      /* ⚠️ 17 Oct 2026 · Munkaidő has no list, and only worklogs and Munkaidő draw over time —
         a layout the new source cannot take falls back to the summary. */
      const timelineRows = TIMELINE_DIMENSIONS_FOR[value];
      const nextLayout =
        (value === 'workday' && layout === 'list') || (isTimeline && timelineRows === undefined)
          ? 'summary'
          : layout;
      const needsRange =
        (value === 'workday' || nextLayout === 'timeline') &&
        (draft.periodFrom === null || draft.periodTo === null);
      const rolling = periodRange('last3Months', props.today);
      props.onDraft({
        source: value,
        layout: nextLayout,
        ...(nextLayout === 'list' && layout === 'list'
          ? { columns: [...DEFAULT_REPORT_COLUMNS[value]], sort: null }
          : {}),
        ...(timelineRows === undefined
          ? {}
          : {
              timeline: {
                ...spec,
                rows:
                  spec.rows.filter((row) => timelineRows.includes(row)).length === 0
                    ? ['assignee']
                    : spec.rows.filter((row) => timelineRows.includes(row)),
                extras: spec.extras.filter((extra) =>
                  (TIMELINE_EXTRA_DIMENSIONS_FOR[value] ?? []).includes(extra.dimension),
                ),
              },
            }),
        ...(needsRange
          ? { period: 'last3Months' as const, periodFrom: rolling.from, periodTo: rolling.to }
          : {}),
        filters: compatibleFilters(draft.filters ?? {}, draft.source, value),
        ...(dimensions.includes(draft.dimension)
          ? {}
          : { dimension: value === 'workday' ? ('assignee' as const) : ('project' as const) }),
        metric:
          draft.metric !== null && !metricsFor(value).includes(draft.metric)
            ? value === 'workday'
              ? 'missing'
              : 'hours'
            : draft.metric,
      });
    },
  }));

  /* ⚠️ Only what the source can reach — a dimension it cannot would group everything into the
     `'none'` row, which reads as a broken report rather than as an impossible question. */
  const dimensionMenu: MenuItem[] = DIMENSIONS_FOR[draft.source].map((value) => ({
    label: dimensionLabel(t, { ...draft, metric: 'hours', dimension: value }),
    tourId: `dimension-${value}`,
    checked: draft.dimension === value,
    /**
     * ⚠️ **Disabled when there is no `select` metafield to group by**, rather than offered and then
     * refused. The dimension needs a field id, and there is no honest default — the server would
     * return `validation_failed` on a menu item somebody was invited to click.
     */
    disabled: value === 'metafield' && props.metafields.length === 0,
    onSelect: () => {
      props.onDraft({
        dimension: value,
        dimensionMetafieldId: value === 'metafield' ? (props.metafields[0]?.id ?? null) : null,
      });
    },
  }));

  /**
   * ⚠⚠ **TWENTY-FIVE, IN TWO GROUPS, AND A REFUSED ONE IS DISABLED RATHER THAN HIDDEN.** A
   * reader who came looking for a sunburst should find out it needs a measure with something to
   * compare against — not conclude the product does not have one. `chartRefusal` says which of the
   * three reasons it is, and the label carries it.
   */
  const chartMenu: MenuItem[] = REPORT_CHARTS.reduce<MenuItem[]>((items, value, index) => {
    const group = CHART_GROUP_OF[value];
    const previous = index === 0 ? null : CHART_GROUP_OF[REPORT_CHARTS[index - 1] as ReportChart];
    if (group !== previous) {
      if (index > 0) items.push({ type: 'separator' });
      items.push({
        type: 'label',
        label:
          group === 'featured' ? t('report.chart.groupFeatured') : t('report.chart.groupBasic'),
      });
    }

    const refusal =
      draft.metric === null
        ? null
        : chartRefusal(value, {
            metric: draft.metric,
            hasDays: props.snapshot !== null && props.snapshot.days.length > 0,
            hasMatrix: props.snapshot !== null && props.snapshot.matrix.months.length > 0,
          });

    items.push({
      label: chartLabel(t, value),
      ...(refusal === null ? {} : { description: refusalNote(t, refusal) }),
      checked: draft.chart === value,
      disabled: refusal !== null,
      onSelect: () => {
        props.onDraft({ chart: value });
      },
    });
    return items;
  }, []);

  /**
   * ⚠️ **Derived from the SNAPSHOT, not from the definition.** "Seven categories" and "one
   * category is 68% of it" are facts about the result; a suggestion made from the definition alone
   * would be guessing at the shape of data it has not seen.
   */
  const recommendations = useMemo(() => {
    if (snapshot === null || draft.metric === null) return [];
    const total = Number(snapshot.total);
    const biggest = snapshot.rows[0];
    return recommendCharts({
      chart: draft.chart,
      metric: draft.metric,
      dimension: draft.dimension,
      groupCount: snapshot.groupCount,
      topShare: total === 0 || biggest === undefined ? 0 : Math.abs(Number(biggest.value) / total),
      hasDays: snapshot.days.length > 0,
      hasMatrix: snapshot.matrix.months.length > 0,
      monthCount: snapshot.matrix.months.length,
    });
  }, [snapshot, draft.chart, draft.metric, draft.dimension]);

  /**
   * ⚠️ 17 Oct 2026 · **A PRESET IS KEPT, SO A SAVED REPORT MOVES WITH THE CALENDAR.** *All time* is
   * not offered where columns or working days need a left edge — a timeline, and Munkaidő.
   */
  const bounded = isTimeline || draft.source === 'workday';
  const periodMenu: MenuItem[] = REPORT_PERIODS.filter((value) => value !== 'custom').map(
    (value) => ({
      label: periodLabelOf(t, value),
      checked: period === value,
      disabled: value === 'all' && bounded,
      onSelect: () => {
        const range = periodRange(value, props.today);
        props.onDraft({
          periodFrom: range.from,
          periodTo: range.to,
          period: value === 'all' ? undefined : value,
        });
      },
    }),
  );

  /* ── the timeline's rail — rows, the width of a column, and the extras ────────────────────── */
  const timelineDimensions: readonly TimelineDimension[] =
    TIMELINE_DIMENSIONS_FOR[draft.source] ?? [];
  const extraDimensions: readonly TimelineDimension[] =
    TIMELINE_EXTRA_DIMENSIONS_FOR[draft.source] ?? [];
  const setSpec = (next: Partial<TimelineSpec>): void => {
    const merged: TimelineSpec = { ...spec, ...next };
    props.onDraft({
      timeline: {
        ...merged,
        /* ⚠️ An extra over a dimension that is now a row would count the row itself. */
        extras: merged.extras.filter((extra) => !merged.rows.includes(extra.dimension)),
      },
    });
  };
  const extraMenu: MenuItem[] = extraDimensions
    .filter((dimension) => !spec.rows.includes(dimension))
    .flatMap((dimension) =>
      TIMELINE_EXTRA_KINDS.filter(
        (kind) =>
          !spec.extras.some((extra) => extra.kind === kind && extra.dimension === dimension),
      ).map((kind) => ({
        label: extraHeading(t, kind, dimension),
        onSelect: () => {
          setSpec({ extras: [...spec.extras, { kind, dimension }] });
        },
      })),
    );

  const filterAddMenu: MenuItem[] = ([...REPORT_FILTERS_FOR[draft.source], 'metafield'] as const)
    .filter((key) => !filters.includes(key))
    .filter((key) => key !== 'metafield' || props.metafields.length > 0)
    .map((key) => ({
      label: filterName(t, key, draft.source),
      icon: 'funnel',
      onSelect: () => {
        props.onFilters([...filters, key]);
      },
    }));

  const definition = asDefinition(draft);

  /**
   * ⚠️⚠️ **THE BUILDER'S BAR IS THE SHELL'S, AND IT USED TO BE A SECOND ONE.** `markup.html`
   * 168–180 puts the whole of this in the 48px chrome: back, the name edited IN PLACE, refresh, the
   * kebab, `ran_at`, then *Vezérlők* and *Mentés* on the right. Ours drew it ten pixels under the
   * chrome's own title, so the screen opened with *Riportok* above a report's name.
   *
   * ⚠️ **The name input keeps its `aria-label`.** It is the page's title and it is a text field, so
   * nothing else names it — and in the header there is no visible label to borrow.
   */
  usePageHeader(
    () => (
      <>
        <Button
          variant="ghost"
          size="icon"
          onClick={props.onBack}
          aria-label={t('report.action.back')}
        >
          <Icon name="chevron-left" size={16} />
        </Button>

        {/* ⚠️ The name is edited IN the header, which is the mockup's own shape — its kebab's
         *"Átnevezés"* only says so in a toast.

           3 Nov 2026 · a saved report's name is saved when the field is left or takes Enter;
           Escape puts the stored one back. ⚠️ The handlers read `latest`, because this node is
           rebuilt only when a dependency below changes and a closure from then would carry an
           old version. */}
        <TextInput
          value={props.name}
          onChange={(event) => {
            props.onName(event.target.value);
          }}
          onBlur={() => {
            if (cancelling.current) {
              cancelling.current = false;
              return;
            }
            latest.current.onNameCommit();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.blur();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              cancelling.current = true;
              latest.current.onNameCancel();
              event.currentTarget.blur();
            }
          }}
          aria-label={t('report.field.name')}
          data-tour="report-name"
          className="h-8 max-w-[280px] min-w-[120px] border-transparent bg-transparent px-1.5 text-[13.5px] font-medium"
        />

        {/**
         * ⚠️⚠️ **`ran_at`, SHOWN, BECAUSE P-3 IS WHY REFRESH EXISTS.** A saved report holds a stored
         * snapshot; without the timestamp beside it nobody can tell a live figure from a month-old
         * one, and Refresh becomes a button with no visible purpose.
         */}
        {props.ranAt !== null && (
          <span className="text-muted-foreground text-[11.5px] tabular-nums">
            {t('report.ranAt', { at: props.ranAt.slice(0, 16).replace('T', ' ') })}
          </span>
        )}

        <div className="flex-1" />

        {/* 21 Oct 2026 · the product owner's order: refresh and the kebab on the right, then
            export and save — *Vezérlők* went down into the filter bar with the other controls. */}
        {props.reportId !== null && (
          <>
            {/* 3 Nov 2026 · pin from the report itself, as the project page does — the same state
                as the list's row menu (`useSidebarContext`), so the two cannot disagree. A saved
                report only: an unsaved one has nothing to pin. ⚠️ 4 Nov 2026 · beside Refresh with
                the other header actions, not after the name; the kit Tooltip, never `title=`. */}
            {sidebar === null ? null : (
              <Tooltip content={pinned ? t('report.action.unpin') : t('report.action.pin')}>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={pinned ? t('report.action.unpin') : t('report.action.pin')}
                  aria-pressed={pinned}
                  onClick={() => {
                    const reportId = latest.current.reportId;
                    if (reportId !== null) sidebar.setReportPinned(reportId, !pinned);
                  }}
                >
                  <Icon
                    name="star"
                    size={15}
                    className={
                      pinned ? 'fill-current text-[var(--status-warn)]' : 'text-muted-foreground'
                    }
                  />
                </Button>
              </Tooltip>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={props.onRefresh}
              aria-label={t('report.action.refresh')}
            >
              <Icon name="refresh-cw" size={14} />
            </Button>
            <DropdownMenu items={kebab} align="end" width={230} label={t('report.actions')}>
              <Button variant="ghost" size="icon" aria-label={t('report.actions')}>
                <Icon name="ellipsis" size={16} />
              </Button>
            </DropdownMenu>
          </>
        )}
        {/* ⚠️ The shared export dialog: XLSX or CSV, chosen there. Absent until the report is
            saved, or for somebody without `report.export`. */}
        {props.onExport === null ? null : (
          <Button variant="outline" size="sm" onClick={props.onExport} data-tour="report-export">
            <Icon name="download" size={14} />
            {t('report.action.exportFile')}
          </Button>
        )}
        {props.canManage && (
          <Button
            size="sm"
            disabled={definition === null}
            onClick={props.onSave}
            data-tour="report-save"
          >
            {t('common.save')}
          </Button>
        )}
      </>
    ),
    /**
     * ⚠️ **VALUES ONLY, and `props.name` is one of them** — the header has to re-render as somebody
     * types the report's name. Every callback here is a fresh closure each render and is reached
     * through the ref instead; including one froze the whole app once, and `page-header.tsx` has
     * the loop written out.
     */
    [
      props.name,
      props.reportId,
      props.ranAt,
      props.canManage,
      props.onExport === null,
      definition === null,
      pinned,
      sidebar === null,
    ],
  );

  return (
    <div
      /* ⚠️ Two markers for two states of one screen — `routes.tsx` says why. */
      data-screen={props.reportId === null ? 'report-new' : 'report-builder'}
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      {/* ── the header ───────────────────────────────────────────────────────────────────── */}
      {/* ── the filter bar ───────────────────────────────────────────────────────────────── */}
      <div
        className="flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-2"
        data-control-row
      >
        <DropdownMenu
          items={periodMenu}
          align="start"
          width={220}
          label={t('report.column.period')}
        >
          <Button variant="outline" size="sm" className="gap-1.5" data-tour="report-period">
            <Icon name="calendar" size={14} className="text-muted-foreground" />
            {periodLabelOf(t, period)}
            <Icon name="chevron-down" size={13} className="opacity-60" />
          </Button>
        </DropdownMenu>

        <DropdownMenu
          items={sourceMenu}
          align="start"
          width={220}
          label={t('report.column.source')}
        >
          <Button variant="outline" size="sm" className="gap-1.5" data-tour="report-source">
            <Icon name="database" size={14} className="text-muted-foreground" />
            {sourceLabel(t, draft.source)}
            <Icon name="chevron-down" size={13} className="opacity-60" />
          </Button>
        </DropdownMenu>

        {/**
         * ⚠️⚠️ 16 Oct 2026 · **ÖSSZESÍTŐ OR LISTA — WHAT A ROW IS.** A summary groups and draws; a
         * list shows every record with its columns. Switching to a list for the first time gives
         * it the source's default columns, so the screen is never an empty table.
         */}
        <LayoutSwitch
          value={layout}
          /* ⚠️ 17 Oct 2026 · Munkaidő has no list; only worklogs and Munkaidő draw over time. */
          disabled={[
            ...(draft.source === 'workday' ? (['list'] as const) : []),
            ...(TIMELINE_DIMENSIONS_FOR[draft.source] === undefined ? (['timeline'] as const) : []),
          ]}
          onChange={(next) => {
            if (next === 'list') {
              props.onDraft({
                layout: next,
                columns:
                  (draft.columns ?? []).length > 0
                    ? draft.columns
                    : [...DEFAULT_REPORT_COLUMNS[draft.source]],
              });
              return;
            }
            if (next === 'timeline') {
              /* A timeline needs a measure, its rows and a range with two ends. */
              const rolling = periodRange('last3Months', props.today);
              props.onDraft({
                layout: next,
                timeline: draft.timeline ?? DEFAULT_TIMELINE,
                metric: draft.metric ?? (draft.source === 'workday' ? 'missing' : 'hours'),
                ...(draft.periodFrom === null || draft.periodTo === null
                  ? {
                      period: 'last3Months' as const,
                      periodFrom: rolling.from,
                      periodTo: rolling.to,
                    }
                  : {}),
              });
              return;
            }
            props.onDraft({ layout: next });
          }}
        />

        <div className="flex items-center gap-1.5">
          <DateField
            label={t('report.field.from')}
            value={draft.periodFrom}
            className="w-[142px]"
            buttonClassName="h-7 text-[12px]"
            onChange={(from) => {
              props.onDraft({ periodFrom: from === '' ? null : from, period: undefined });
            }}
          />
          <span className="text-muted-foreground text-[12px]">–</span>
          <DateField
            label={t('report.field.to')}
            value={draft.periodTo}
            className="w-[142px]"
            buttonClassName="h-7 text-[12px]"
            onChange={(to) => {
              props.onDraft({ periodTo: to === '' ? null : to, period: undefined });
            }}
          />
        </div>

        {/**
         * ⚠️⚠️ **THE PERIOD SAYS WHICH COLUMN IT FILTERS, AND THIS LINE IS NOT DECORATION.** On a
         * worklog report it is `worked_on`; on a task report it is `due_on`, which the mockup does
         * not decide and whose consequence surprises — a task with no due date falls outside every
         * bounded period. A date range with no stated column is the kind of control somebody
         * mistrusts once and then works around.
         */}
        <span className="text-muted-foreground text-[11.5px]">
          {draft.source === 'worklog'
            ? t('report.period.byWorkedOn')
            : draft.source === 'workday'
              ? t('report.period.byWorkday')
              : draft.source === 'allocation'
                ? t('report.period.byAllocationDay')
                : t('report.period.byDueOn')}
        </span>

        {/* ⚠️ The mockup renders the inverted range as a note and runs anyway; the server refuses it,
            so the builder says so here rather than letting Save fail. */}
        {draft.periodFrom !== null &&
          draft.periodTo !== null &&
          draft.periodTo < draft.periodFrom && (
            <span role="alert" className="text-destructive text-[11.5px]">
              {t('report.error.inverted')}
            </span>
          )}

        <div className="flex-1" />
        {/* 21 Oct 2026 · with the other controls, on their row — it was in the page header. */}
        <Button
          variant={props.showControls ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => {
            props.onShowControls(!props.showControls);
          }}
        >
          <Icon name="sliders-horizontal" size={14} />
          {t('report.controls')}
        </Button>
      </div>

      {props.serverError !== null && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mx-5 mt-3 rounded-md border px-3 py-2 text-[12.5px]"
        >
          {props.serverError}
        </p>
      )}

      {/* ── the result and the rail ──────────────────────────────────────────────────────── */}
      <div
        data-tour="report-body"
        className={[
          'grid min-h-0 flex-1 gap-0 overflow-hidden',
          props.showControls ? 'xl:grid-cols-[minmax(0,1fr)_300px]' : 'grid-cols-1',
        ].join(' ')}
      >
        {/* ⚠️ A list runs EDGE TO EDGE like every other list screen (`clients-screen.tsx`): the
            summary's 20px inset left the table floating in a margin with a seam around it. */}
        <div
          className={
            (isList || isTimeline) && definition !== null
              ? 'flex min-h-0 flex-col overflow-hidden'
              : 'min-h-0 overflow-y-auto p-5'
          }
        >
          {isTimeline && definition !== null ? (
            <ReportTimelineView definition={definition} reloadKey={props.listReloadKey} />
          ) : isList && definition !== null ? (
            <ReportListView
              definition={definition}
              options={props.listOptions}
              canMoney={props.canMoney}
              reloadKey={props.listReloadKey}
              onDefinition={(patch) => {
                props.onDraft(patch);
              }}
              onExport={props.onExport}
            />
          ) : props.moneyLocked ? (
            <MoneyLocked />
          ) : definition === null ? (
            <NoMetric metricMenu={metricMenu} />
          ) : snapshot === null ? (
            /* ⚠️ `state-rules.md`: a per-pane message rather than a shimmer — the layout must not
             * reflow when the numbers arrive. */
            <p className="text-muted-foreground text-[12.5px]">{t('state.loading')}</p>
          ) : (
            <div className={props.isRunning ? 'opacity-60 transition-opacity' : undefined}>
              <ReportResult
                definition={definition}
                snapshot={snapshot}
                showTable={props.showTable}
              />
            </div>
          )}
        </div>

        {props.showControls && (
          <div className="min-h-0 overflow-y-auto border-l p-3">
            <div className="flex flex-col gap-2.5">
              {/* Mértékek — ⚠️ not on a list, whose columns are its content. */}
              {!isList && (
                <>
                  <RailSection
                    icon="chart-column"
                    tourId="report-metrics"
                    title={t('report.rail.metrics')}
                    menu={metricMenu}
                    addLabel={t('report.rail.addMetric')}
                  >
                    {draft.metric === null ? (
                      <p className="text-muted-foreground px-3 py-2.5 text-[12px]">
                        {t('report.rail.noMetric')}
                      </p>
                    ) : (
                      <button
                        type="button"
                        className="hover:bg-accent flex w-full items-center gap-2.5 px-3 py-2 text-left"
                        onClick={() => {
                          props.onDraft({ metric: null });
                        }}
                      >
                        <span className="bg-primary border-primary text-primary-foreground flex size-4 shrink-0 items-center justify-center rounded-[4px] border">
                          <Icon name="check" size={11} />
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[12.5px]">
                          {metricLabel(t, draft.metric)}
                        </span>
                      </button>
                    )}
                  </RailSection>

                  {isTimeline ? (
                    <>
                      {/* 17 Oct 2026 · *Sorok* — one or two row dimensions, and the column width. */}
                      <RailSection icon="tag" tourId="report-rows" title={t('report.rail.rows')}>
                        <div className="flex flex-col gap-1.5 px-3 py-2">
                          <Select
                            value={spec.rows[0] ?? 'assignee'}
                            onChange={(event) => {
                              const first = event.target.value as TimelineDimension;
                              setSpec({
                                rows: [first, ...spec.rows.slice(1).filter((row) => row !== first)],
                              });
                            }}
                            options={timelineDimensions.map((dimension) => ({
                              value: dimension,
                              label: timelineDimensionLabel(t, dimension, draft.source),
                            }))}
                            aria-label={t('report.rail.rowFirst')}
                            className="w-full"
                          />
                          {timelineDimensions.length > 1 && (
                            <Select
                              value={spec.rows[1] ?? ''}
                              onChange={(event) => {
                                const second = event.target.value as TimelineDimension | '';
                                setSpec({
                                  rows:
                                    second === ''
                                      ? spec.rows.slice(0, 1)
                                      : [spec.rows[0] ?? 'assignee', second],
                                });
                              }}
                              options={[
                                { value: '', label: t('report.rail.rowNone') },
                                ...timelineDimensions
                                  .filter((dimension) => dimension !== spec.rows[0])
                                  .map((dimension) => ({
                                    value: dimension,
                                    label: timelineDimensionLabel(t, dimension, draft.source),
                                  })),
                              ]}
                              aria-label={t('report.rail.rowSecond')}
                              className="w-full"
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-2 border-t px-3 py-2">
                          <span className="min-w-0 flex-1 truncate text-[12.5px]">
                            {t('report.rail.granularity')}
                          </span>
                          <Segmented
                            label={t('report.rail.granularity')}
                            value={spec.granularity}
                            options={TIMELINE_GRANULARITIES.map((value: TimelineGranularity) => ({
                              value,
                              label:
                                value === 'week'
                                  ? t('report.granularity.week')
                                  : value === 'month'
                                    ? t('report.granularity.month')
                                    : t('report.granularity.quarter'),
                            }))}
                            onChange={(value) => {
                              setSpec({ granularity: value });
                            }}
                          />
                        </div>
                      </RailSection>

                      {extraDimensions.length > 0 && (
                        <RailSection
                          icon="layout-grid"
                          tourId="report-extras"
                          title={t('report.rail.extras')}
                          menu={extraMenu.length === 0 ? undefined : extraMenu}
                          addLabel={t('report.rail.addExtra')}
                        >
                          {spec.extras.length === 0 ? (
                            <p className="text-muted-foreground px-3 py-2.5 text-[12px]">
                              {t('report.rail.noExtras')}
                            </p>
                          ) : (
                            spec.extras.map((extra, at) => (
                              <div
                                key={`${extra.kind}:${extra.dimension}`}
                                className="flex items-center gap-2 border-b px-3 py-2 last:border-0"
                              >
                                <span className="min-w-0 flex-1 truncate text-[12.5px]">
                                  {extraHeading(t, extra.kind, extra.dimension)}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-6"
                                  aria-label={t('report.rail.removeExtra', {
                                    label: extraHeading(t, extra.kind, extra.dimension),
                                  })}
                                  onClick={() => {
                                    setSpec({
                                      extras: spec.extras.filter((_one, index) => index !== at),
                                    });
                                  }}
                                >
                                  <Icon name="x" size={12} />
                                </Button>
                              </div>
                            ))
                          )}
                        </RailSection>
                      )}
                    </>
                  ) : (
                    <>
                      {/**
                       * ⚠️ **Dimmed until a metric exists, which is the mockup's `repRailDim`.** It uses
                       * `pointer-events-none opacity-45`; we add `aria-disabled` and real `disabled`
                       * controls, because opacity is not audible and a screen reader would otherwise offer
                       * three sections that do nothing.
                       */}
                      <RailSection
                        icon="tag"
                        tourId="report-dimensions"
                        title={t('report.rail.dimensions')}
                        menu={dimensionMenu}
                        addLabel={t('report.rail.pickDimension')}
                        disabled={draft.metric === null}
                      >
                        <DropdownMenu
                          items={dimensionMenu}
                          align="end"
                          width={240}
                          label={t('report.rail.pickDimension')}
                        >
                          <button
                            type="button"
                            disabled={draft.metric === null}
                            className="hover:bg-accent flex w-full items-center gap-2 px-3 py-2 text-left text-[12.5px]"
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {dimensionLabel(t, { ...draft, metric: 'hours' })}
                            </span>
                            <Icon name="chevron-down" size={13} className="opacity-60" />
                          </button>
                        </DropdownMenu>

                        {draft.dimension === 'metafield' && (
                          <div className="border-t px-3 py-2">
                            <Select
                              value={draft.dimensionMetafieldId ?? ''}
                              onChange={(event) => {
                                props.onDraft({ dimensionMetafieldId: event.target.value });
                              }}
                              options={props.metafields.map((field) => ({
                                value: field.id,
                                label: field.name,
                              }))}
                              aria-label={t('report.dim.metafield')}
                              className="w-full"
                            />
                          </div>
                        )}
                      </RailSection>

                      {/* Megjelenítés */}
                      <RailSection
                        icon="layout-grid"
                        tourId="report-display"
                        title={t('report.rail.display')}
                        disabled={draft.metric === null}
                      >
                        <DropdownMenu
                          items={chartMenu}
                          align="end"
                          width={200}
                          label={t('report.rail.display')}
                        >
                          <button
                            type="button"
                            disabled={draft.metric === null}
                            className="hover:bg-accent flex w-full items-center gap-2 border-b px-3 py-2 text-left text-[12.5px]"
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {chartLabel(t, draft.chart)}
                            </span>
                            <Icon name="chevron-down" size={13} className="opacity-60" />
                          </button>
                        </DropdownMenu>
                        <label className="flex items-center gap-2 px-3 py-2 text-[12.5px]">
                          <span className="min-w-0 flex-1 truncate">{t('report.table')}</span>
                          <Switch
                            checked={props.showTable}
                            onCheckedChange={props.onShowTable}
                            label={t('report.table')}
                          />
                        </label>

                        {/**
                         * ⚠⚠ **THE HALF THAT MAKES A TWENTY-FIVE-ENTRY PICKER USABLE.** Without it the
                         * catalogue is a menu of names and people keep choosing the bar, because it is the
                         * one they can predict. Each suggestion reads the SHAPE of the result — how many
                         * groups, how concentrated, whether the dimension has an order — and says why.
                         *
                         * ⚠️ Inside the Display card rather than in its own, because it is a shortcut to the
                         * control directly above it.
                         */}
                        {recommendations.length === 0 ? null : (
                          <div className="flex flex-col gap-1 border-t px-3 py-2">
                            <span className="text-muted-foreground text-[11px]">
                              {t('report.chart.suggested')}
                            </span>
                            {recommendations.map((one) => (
                              <button
                                key={one.chart}
                                type="button"
                                className="hover:bg-accent -mx-1 flex flex-col items-start gap-0.5 rounded-md px-1 py-1 text-left"
                                onClick={() => {
                                  props.onDraft({ chart: one.chart });
                                }}
                              >
                                <span className="text-[12px] font-medium">
                                  {chartLabel(t, one.chart)}
                                </span>
                                <span className="text-muted-foreground text-[11px] leading-snug">
                                  {reasonNote(t, one.reason, snapshot?.groupCount ?? 0)}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </RailSection>
                    </>
                  )}
                </>
              )}

              {/* Szűrők */}
              <RailSection
                icon="funnel"
                tourId="report-filters"
                title={t('report.rail.filters')}
                menu={filterAddMenu.length === 0 ? undefined : filterAddMenu}
                addLabel={t('report.rail.addFilter')}
                /* ⚠️ A list needs no measure, so its filters are never waiting for one. */
                disabled={draft.metric === null && !isList}
              >
                {/**
                 * 26 Oct 2026 · ⚠️ **ALWAYS THERE ON AN ALLOCATION REPORT, AND NOT ADD-ABLE.** The
                 * product owner: *Soft booking: Include / Exclude* — one choice that always applies,
                 * so it is a fixed row rather than a filter somebody has to know to add. Include is
                 * the default and is stored as nothing.
                 */}
                {draft.source === 'allocation' ? (
                  /* ⚠️ The name on its own line above the control, as every other filter row:
                     beside a full-width select it was squeezed to nothing. */
                  <div className="flex flex-col gap-1.5 border-b px-3 py-2 last:border-0">
                    <span className="text-[12.5px]">{t('report.filter.softBookings')}</span>
                    <Select
                      aria-label={t('report.filter.softBookings')}
                      className="w-full"
                      value={draft.filters?.softBookings ?? 'include'}
                      options={[
                        { value: 'include', label: t('report.filter.softInclude') },
                        { value: 'exclude', label: t('report.filter.softExclude') },
                      ]}
                      onChange={(event) => {
                        const rest = { ...(draft.filters ?? {}) };
                        delete rest.softBookings;
                        props.onDraft({
                          filters:
                            event.target.value === 'exclude'
                              ? { ...rest, softBookings: 'exclude' }
                              : rest,
                        });
                      }}
                    />
                  </div>
                ) : null}
                {filters.length === 0 ? (
                  draft.source === 'allocation' ? null : (
                    <p className="text-muted-foreground px-3 py-2.5 text-[12px]">
                      {t('report.rail.noFilters')}
                    </p>
                  )
                ) : (
                  filters.map((key) => (
                    <div
                      key={key}
                      className="flex flex-col gap-1.5 border-b px-3 py-2 last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-[12.5px]">
                          {filterName(t, key, draft.source)}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6"
                          aria-label={t('report.rail.removeFilter', {
                            label: filterName(t, key, draft.source),
                          })}
                          onClick={() => {
                            /* ⚠️ Removing the row CLEARS the value too. Leaving it set would keep a
                             * report filtered by something no longer on screen. */
                            props.onFilters(filters.filter((other) => other !== key));
                            props.onDraft(clearedFilter(key, draft));
                          }}
                        >
                          <Icon name="x" size={12} />
                        </Button>
                      </div>
                      <FilterControl
                        filterKey={key}
                        draft={draft}
                        metafields={props.metafields}
                        listOptions={props.listOptions}
                        onDraft={props.onDraft}
                      />
                    </div>
                  ))
                )}
              </RailSection>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** `markup.html` 1305–1319 — the "start with a metric" card. */
function NoMetric({ metricMenu }: { readonly metricMenu: MenuItem[] }) {
  const t = useT();

  return (
    <div className="bg-card text-card-foreground flex min-h-[420px] flex-col items-center justify-center gap-3 rounded-xl border shadow-sm">
      <span className="bg-muted text-muted-foreground flex size-14 items-center justify-center rounded-xl">
        <Icon name="chart-column" size={24} />
      </span>
      <div className="text-[13px] font-medium">{t('report.noMetric.title')}</div>
      <div className="text-muted-foreground max-w-[320px] text-center text-[12.5px] leading-relaxed">
        {t('report.noMetric.body')}
      </div>
      {/* ⚠️ `align="start"`: the vocabulary is start/end, and a centred popover is not one of the
          two — the mockup's `align="center"` is its own component's option, not ours. */}
      <DropdownMenu items={metricMenu} align="start" width={240} label={t('report.rail.addMetric')}>
        <Button variant="outline">
          <Icon name="plus" size={14} />
          {t('report.rail.addMetric')}
        </Button>
      </DropdownMenu>
    </div>
  );
}

/**
 * 16 Oct 2026 · *Összesítő* / *Lista*. The finance chart's segmented control, in the builder's bar.
 *
 * ⚠️ A radio group to assistive technology — two exclusive states of one setting.
 */
function LayoutSwitch({
  value,
  disabled,
  onChange,
}: {
  readonly value: ReportLayout;
  /** 17 Oct 2026 · the layouts this source cannot take. */
  readonly disabled: readonly ReportLayout[];
  readonly onChange: (value: ReportLayout) => void;
}) {
  const t = useT();
  const options: readonly { value: ReportLayout; label: string }[] = [
    { value: 'summary', label: t('report.layout.summary') },
    { value: 'list', label: t('report.layout.list') },
    { value: 'timeline', label: t('report.layout.timeline') },
  ];
  return (
    <div
      role="radiogroup"
      aria-label={t('report.layout.label')}
      data-tour="report-layout"
      /* ⚠️ The bar's buttons are the kit's `sm` outline buttons — 32px, 13px — and this sits among
         them, so it takes their height, border and type rather than the chart's smaller switch. */
      className="border-input flex h-8 items-center gap-0.5 rounded-md border p-0.5 shadow-xs"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          disabled={disabled.includes(option.value)}
          className={`h-full rounded-[5px] px-2.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
            value === option.value
              ? 'bg-accent text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => {
            onChange(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** One bordered block of the rail, with an optional `+` menu in its header. */
function RailSection({
  icon,
  title,
  menu,
  addLabel,
  disabled = false,
  tourId,
  children,
}: {
  /** A stable hook for the guided tour; the add button gets `<tourId>-add`. */
  readonly tourId?: string;
  readonly icon: 'chart-column' | 'tag' | 'layout-grid' | 'funnel';
  readonly title: string;
  readonly menu?: MenuItem[];
  readonly addLabel?: string;
  readonly disabled?: boolean;
  readonly children: React.ReactNode;
}) {
  return (
    <section
      /* ⚠️ A named region, so the rail's four sections are navigable and each `aria-disabled` says
         which one it belongs to. A bare `<section>` has no role at all. */
      aria-label={title}
      aria-disabled={disabled || undefined}
      data-tour={tourId}
      className={['overflow-hidden rounded-lg border', disabled ? 'opacity-45' : ''].join(' ')}
    >
      <div className="bg-muted/40 flex items-center gap-2 border-b px-3 py-2">
        <Icon name={icon} size={13} className="text-muted-foreground" />
        <span className="flex-1 text-[12.5px] font-medium">{title}</span>
        {menu !== undefined && addLabel !== undefined && (
          <DropdownMenu items={menu} align="end" width={240} label={addLabel}>
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label={addLabel}
              disabled={disabled}
              data-tour={tourId === undefined ? undefined : `${tourId}-add`}
            >
              <Icon name="plus" size={14} />
            </Button>
          </DropdownMenu>
        )}
      </div>
      {children}
    </section>
  );
}

function filterName(t: Translate, key: FilterKey, source: ReportSource): string {
  if (key === 'metafield') return t('report.filter.metafield');
  return filterLabel(t, key, source);
}

/** What removing a filter row has to clear. */
function clearedFilter(key: FilterKey, draft: BuilderDraft): Partial<BuilderDraft> {
  if (key === 'metafield') return { filterMetafieldId: null, filterMetafieldValue: null };
  return { filters: { ...(draft.filters ?? {}), [key]: undefined } };
}

/**
 * The filters that survive a change of source.
 *
 * ⚠️ A key the new source does not honour goes, and so does `statuses` across the task / money
 * line — a task status id and a contract status are not the same kind of value.
 */
export function compatibleFilters(
  filters: ReportFilters,
  from: ReportSource,
  to: ReportSource,
): ReportFilters {
  const taskBased = (source: ReportSource): boolean => source === 'worklog' || source === 'task';
  const next: ReportFilters = { search: filters.search };
  for (const key of REPORT_FILTERS_FOR[to]) {
    if (key === 'statuses' && taskBased(from) !== taskBased(to)) continue;
    if (filters[key] !== undefined) next[key] = filters[key];
  }
  return next;
}

function FilterControl({
  filterKey,
  draft,
  metafields,
  listOptions,
  onDraft,
}: {
  readonly filterKey: FilterKey;
  readonly draft: BuilderDraft;
  readonly metafields: readonly Metafield[];
  readonly listOptions: ReportListOptions;
  readonly onDraft: (patch: Partial<BuilderDraft>) => void;
}) {
  const t = useT();

  if (filterKey !== 'metafield') {
    /* ⚠️ 16 Oct 2026 · several values each, the same rows for the summary and the list. */
    const values = draft.filters?.[filterKey] ?? [];
    return (
      <MultiSelect
        searchable
        searchPlaceholder={t('list.searchOptions')}
        placeholder={t('common.all')}
        label={filterName(t, filterKey, draft.source)}
        values={values}
        onValuesChange={(next) => {
          onDraft({
            filters: {
              ...(draft.filters ?? {}),
              [filterKey]: next.length === 0 ? undefined : next,
            },
          });
        }}
        options={filterOptions(t, filterKey, draft.source, listOptions)}
        className="w-full"
      />
    );
  }

  /**
   * The metafield filter is two controls: which field, and which of its options.
   *
   * ⚠️ **The value list comes from the field's own `options`**, so a filter cannot be set to a value
   * the field does not have. The mockup hardcodes its three risk levels; a `select` metafield carries
   * them, so nothing needs inventing.
   */
  const field = metafields.find((candidate) => candidate.id === draft.filterMetafieldId);

  return (
    <div className="flex flex-col gap-1.5">
      <Select
        value={draft.filterMetafieldId ?? ''}
        onChange={(event) => {
          /* ⚠️ Changing the field clears the value: an option of the old field is not an option of
           * the new one, and the server would refuse the pair. */
          onDraft({
            filterMetafieldId: event.target.value === '' ? null : event.target.value,
            filterMetafieldValue: null,
          });
        }}
        options={[
          { value: '', label: t('common.all') },
          ...metafields.map((candidate) => ({ value: candidate.id, label: candidate.name })),
        ]}
        aria-label={t('report.filter.metafield')}
        className="w-full"
      />
      {field !== undefined && (
        <Select
          value={draft.filterMetafieldValue ?? ''}
          onChange={(event) => {
            onDraft({
              filterMetafieldValue: event.target.value === '' ? null : event.target.value,
            });
          }}
          options={[
            { value: '', label: t('common.all') },
            ...field.options.map((option) => ({ value: option, label: option })),
          ]}
          aria-label={t('report.filter.metafieldValue')}
          className="w-full"
        />
      )}
    </div>
  );
}

export type { ReportDimension };

/**
 * The reason a shape is suggested, as a sentence.
 *
 * ⚠️ One literal key per reason — a computed one is invisible to the catalogue check.
 */
function reasonNote(
  t: ReturnType<typeof useT>,
  reason: RecommendationReason,
  groups: number,
): string {
  if (reason === 'single') return t('report.reco.single');
  if (reason === 'ordered') return t('report.reco.ordered');
  if (reason === 'rhythm') return t('report.reco.rhythm');
  if (reason === 'many') return t('report.reco.many', { count: groups });
  if (reason === 'few') return t('report.reco.few', { count: groups });
  if (reason === 'dominant') return t('report.reco.dominant');
  if (reason === 'companion') return t('report.reco.companion');
  return t('report.reco.months');
}

/** ⚠️ One icon per source, and the three money ones borrow the shapes their screens already use. */
const SOURCE_ICON: Readonly<
  Record<ReportSource, 'clock' | 'list-checks' | 'file-text' | 'repeat' | 'layout-grid'>
> = {
  worklog: 'clock',
  task: 'list-checks',
  contract: 'file-text',
  lineitem: 'list-checks',
  occurrence: 'repeat',
  /* 17 Oct 2026 · Munkaidő — a person's working time. */
  workday: 'clock',
  /* 26 Oct 2026 · Erőforrások — the resource grid's own icon. */
  allocation: 'layout-grid',
};

/** Which measures a source can carry. ⚠️ Mirrors the schema's refinements, which are the rule. */
function metricsFor(source: ReportSource): readonly ReportMetric[] {
  if (source === 'worklog') return ['hours', 'count', 'overtime', 'overtimeCost'];
  if (source === 'task') return ['hours', 'count', 'estimate'];
  if (source === 'workday') return WORKDAY_METRICS;
  if (source === 'allocation') return ALLOCATION_METRICS;
  return ['hours', 'count', 'revenue', 'budget'];
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C60 · *Pénzes riporthoz jogosultság kell*.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE ONLY WAY TO REACH THIS IS A SAVED REPORT.** The measure menu disables what the caller
 * may not pick, so nobody can build their way here — but a report saved while its author held
 * `report.money`, opened after they lost it, lands exactly here. A generic *"that is not available
 * right now"* would read as a broken screen and send them to support; the shield and the sentence
 * tell them the one thing they can actually do, which is ask their admin.
 *
 * ⚠️ **The definition is still on screen behind it.** They can see WHAT the report asks for and
 * change the measure to one they may use — hiding the whole builder would make a report they own
 * unreadable as well as unrunnable.
 */
function MoneyLocked() {
  const t = useT();
  return (
    <div className="bg-card text-card-foreground rounded-xl border shadow-sm">
      <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
        <span className="bg-destructive/10 text-destructive flex size-12 items-center justify-center rounded-xl">
          <Icon name="shield-check" size={20} />
        </span>
        <div className="text-[13px] font-medium">{t('report.money.lockedTitle')}</div>
        <div className="text-muted-foreground max-w-[400px] text-[12.5px] leading-relaxed">
          {t('report.money.lockedNote')}
        </div>
      </div>
    </div>
  );
}
